import React, { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Scan, Fuel, Sparkles, ChefHat, Gift, Bell } from 'lucide-react';

const TOUR_STEPS = [
  {
    icon: Scan,
    color: 'from-green-500 to-emerald-600',
    title: 'Scan & Compare Prices',
    description: 'Scan any barcode to instantly compare prices across 10+ UK supermarkets. Find the cheapest place to shop.',
  },
  {
    icon: Fuel,
    color: 'from-orange-500 to-red-500',
    title: 'Fuel Price Finder',
    description: 'Find the cheapest fuel near you. Set price alerts so you never overpay at the pump.',
  },
  {
    icon: ChefHat,
    color: 'from-amber-500 to-yellow-500',
    title: 'Kitchen AI',
    description: 'Tell us what ingredients you have and get smart meal suggestions. Plan your week and reduce food waste.',
  },
  {
    icon: Bell,
    color: 'from-blue-500 to-indigo-500',
    title: 'Price Alerts',
    description: 'Set alerts on any product. We\'ll notify you the moment the price drops at any retailer.',
  },
  {
    icon: Gift,
    color: 'from-purple-500 to-pink-500',
    title: 'Referrals & Rewards',
    description: 'Share PricePAL with friends — help build a bigger community price database. Our loyalty rewards programme is being refreshed and will return soon.',
  },
];

const WelcomeTour = ({ userData, onClose }) => {
  const [step, setStep] = useState(0);
  const currentStep = TOUR_STEPS[step];
  const Icon = currentStep.icon;

  const tierName = userData?.subscription_tier || 'FREE';
  const isAlpha = userData?.is_alpha_tester || tierName === 'ALPHA_TESTER';
  const isVIP = tierName === 'VIP_FOUNDER';
  const isEarly = tierName === 'EARLY_FOUNDER';

  const getTierMessage = () => {
    if (isAlpha) return { label: 'Alpha Tester', detail: 'Lifetime FREE access to all Pro features.', emoji: '👑' };
    if (isVIP) return { label: 'VIP Founder', detail: 'Exclusive VIP pricing — £1.00/mo locked in for life.', emoji: '💎' };
    if (isEarly) return { label: 'Early Founder', detail: 'Early Founder pricing — £1.99/mo locked in for life.', emoji: '🚀' };
    return { label: 'Welcome', detail: 'You\'re on your 7-day Premium trial — explore everything!', emoji: '🎉' };
  };

  const tierInfo = getTierMessage();

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="welcome-tour-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="welcome-tour-modal">
        {/* Close button */}
        <div className="flex justify-end p-3 pb-0">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition" data-testid="tour-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Welcome header (first view only) */}
        {step === 0 && (
          <div className="text-center px-6 pb-4">
            <div className="text-5xl mb-3">{tierInfo.emoji}</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1" data-testid="tour-tier-label">
              {tierInfo.label}
            </h2>
            <p className="text-gray-600 text-sm">{tierInfo.detail}</p>
            <div className="mt-3 h-px bg-gray-200" />
          </div>
        )}

        {/* Step content */}
        <div className="px-6 pb-4">
          <div className={`bg-gradient-to-br ${currentStep.color} rounded-xl p-6 text-white text-center mb-4`}>
            <Icon className="w-12 h-12 mx-auto mb-3 opacity-90" />
            <h3 className="text-xl font-bold mb-2">{currentStep.title}</h3>
            <p className="text-sm opacity-90">{currentStep.description}</p>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-4">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all ${i === step ? 'bg-green-500 w-6' : 'bg-gray-300'}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold text-gray-600 hover:bg-gray-50 transition flex items-center justify-center gap-1"
                data-testid="tour-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            {step < TOUR_STEPS.length - 1 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition flex items-center justify-center gap-1"
                data-testid="tour-next-btn"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
                data-testid="tour-done-btn"
              >
                Start Saving!
              </button>
            )}
          </div>

          {/* Skip link */}
          {step < TOUR_STEPS.length - 1 && (
            <button
              onClick={onClose}
              className="w-full text-center text-sm text-gray-400 hover:text-gray-600 mt-3 transition"
              data-testid="tour-skip-btn"
            >
              Skip tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
